import React, { useEffect } from 'react';
import { Form, Field } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import Button from '../../../components/atoms/Button';
import { ButtonKind } from '../../../components/atoms/Button/enums';
import { FormInput } from '../../../components/atoms/Fields/Input';
import { FormAvatarUpload } from '../../../components/molecules/AvatarUpload';
import ScCard from '../../../components/atoms/ScCard';
import ScSectionHeader from '../../../components/molecules/ScSectionHeader';
import useHeaderTitleContext from '../../../hooks/useHeaderTitleContext';
import useLanguageContext from '../../../hooks/useLanguageContext';
import useUserContext from '../../../hooks/useUserContext';
import SupportedLanguages from '../../../setup/intl/enums';
import { languageRadioItems } from './consts';
import { useAccountFormSubmitHandler } from './hooks';
import { AccountFormValues } from './types';

// ── Layout ───────────────────────────────────────────────────────────────────

const StyledMainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--s-6) var(--s-5);
  align-items: start;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StyledFullWidthRow = styled.div`
  grid-column: 1 / -1;
`;

const StyledLogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: var(--s-1);
`;

const StyledLogoHint = styled.p`
  margin: 6px 0 0;
  font-size: var(--t-xs);
  color: var(--muted);
`;

const StyledLogoLabel = styled.label`
  display: block;
  font-size: var(--t-sm);
  font-weight: 600;
  color: var(--ink-2);
  margin-bottom: 8px;
`;

// ── Language card selector ────────────────────────────────────────────────────

const StyledSectionLabel = styled.div`
  margin-bottom: 10px;
  font-size: var(--t-sm);
  font-weight: 600;
  color: var(--ink-2);
`;

const StyledLangGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  max-width: 560px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StyledLangCard = styled.label<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border-radius: var(--r-md);
  border: 1px solid
    ${({ selected }) => (selected ? 'var(--brand-green)' : 'var(--line)')};
  background: ${({ selected }) =>
    selected ? 'var(--success-soft)' : 'var(--surface)'};
  cursor: pointer;
  position: relative;
  transition: border-color var(--dur-fast) var(--ease-out),
    background var(--dur-fast) var(--ease-out);

  &:hover {
    border-color: var(--brand-green);
  }
`;

const StyledLangRadio = styled.div<{ selected: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 9px;
  border: 1px solid
    ${({ selected }) => (selected ? 'var(--brand-green)' : 'var(--line-2)')};
  background: ${({ selected }) => (selected ? 'var(--brand-green)' : 'var(--surface)')};
  flex-shrink: 0;
  display: grid;
  place-items: center;
  transition: border-color var(--dur-fast) var(--ease-out),
    background var(--dur-fast) var(--ease-out);
`;

const StyledLangRadioDot = styled.span<{ selected: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 3px;
  background: #fff;
  opacity: ${({ selected }) => (selected ? 1 : 0)};
`;

const StyledLangBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StyledLangName = styled.span`
  font-size: var(--t-md);
  font-weight: 600;
  color: var(--ink);
`;

const StyledLangMeta = styled.span`
  font-size: var(--t-xs);
  color: var(--muted);
`;

// ── Action row ────────────────────────────────────────────────────────────────

const StyledActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: var(--s-7);
  padding-top: var(--s-4);
  border-top: 1px solid var(--line);
`;

// ── Language metadata map ─────────────────────────────────────────────────────

const LANG_META: Record<SupportedLanguages, { flag: string; meta: string }> = {
  [SupportedLanguages.English]: { flag: '🇬🇧', meta: 'LTR · default' },
  [SupportedLanguages.Arabic]: { flag: '🇮🇶', meta: 'RTL · العربية' },
};

// ── Component ─────────────────────────────────────────────────────────────────

const AccountDetailsPage: React.FC = () => {
  const { user, isUserLoading } = useUserContext();
  const { setHeaderTitle } = useHeaderTitleContext();
  const { setLanguage } = useLanguageContext();
  const intl = useIntl();
  const submitHandler = useAccountFormSubmitHandler();

  useEffect(() => {
    setHeaderTitle(
      intl.formatMessage({
        id: 'sidebar.settings.accountDetails',
        defaultMessage: 'Account details',
      })
    );
  }, [intl, setHeaderTitle]);

  const {
    companyAddress,
    companyName,
    emailAddress,
    language: accountLanguage,
    phoneNumber,
    logo,
  } = user?.accountDetails || {};

  const langItems = languageRadioItems(intl);

  return (
    <ScCard style={{ padding: 'var(--s-6)' }}>
      <ScSectionHeader
        title={intl.formatMessage({ id: 'sidebar.settings.accountDetails', defaultMessage: 'Account details' })}
        subtitle={intl.formatMessage({ id: 'accountDetails.subtitle', defaultMessage: 'Information shown to your team and on receipts.' })}
      />

      <Form<AccountFormValues>
        onSubmit={submitHandler}
        initialValues={{
          companyAddress: companyAddress || '',
          companyName: companyName || '',
          emailAddress: emailAddress || user?.email || '',
          language: accountLanguage || SupportedLanguages.English,
          phoneNumber: phoneNumber || user?.phoneNumber || '',
          logo: logo || undefined,
        }}
      >
        {({ handleSubmit, submitting, values }) => (
          <form onSubmit={handleSubmit}>
            <StyledMainGrid>
              <FormInput
                name='companyName'
                label={intl.formatMessage({
                  id: 'forms.fields.companyName',
                  defaultMessage: 'Company Name',
                })}
                disabled={isUserLoading || submitting}
              />
              <FormInput
                name='companyAddress'
                label={intl.formatMessage({
                  id: 'forms.fields.companyAddress',
                  defaultMessage: 'Company address',
                })}
                disabled={isUserLoading || submitting}
              />
              <FormInput
                name='emailAddress'
                label={intl.formatMessage({
                  id: 'forms.fields.emailAddress',
                  defaultMessage: 'Email address',
                })}
                disabled={isUserLoading || submitting}
                type='email'
              />
              <FormInput
                name='phoneNumber'
                label={intl.formatMessage({
                  id: 'forms.fields.phoneNumber',
                  defaultMessage: 'Phone number',
                })}
                disabled={isUserLoading || submitting}
                type='tel'
              />

              {/* Logo upload — full-width row */}
              <StyledFullWidthRow>
                <StyledLogoLabel>
                  <FormattedMessage id='common.logo' defaultMessage='Logo' />
                </StyledLogoLabel>
                <StyledLogoRow>
                  <FormAvatarUpload
                    name='logo'
                    label={
                      values.logo
                        ? intl.formatMessage({ id: 'common.changeLogo', defaultMessage: 'Change Logo' })
                        : intl.formatMessage({ id: 'common.addLogo', defaultMessage: 'Add Logo' })
                    }
                  />
                  <StyledLogoHint>{intl.formatMessage({ id: 'accountDetails.logoHint', defaultMessage: 'PNG or SVG · 1 MB max' })}</StyledLogoHint>
                </StyledLogoRow>
              </StyledFullWidthRow>

              {/* Language selector — full-width row */}
              <StyledFullWidthRow>
                <StyledSectionLabel>
                  <FormattedMessage
                    id='common.preferredSystemLanguage'
                    defaultMessage='Preferred system language'
                  />
                </StyledSectionLabel>
                <Field<SupportedLanguages> name='language'>
                  {({ input }) => (
                    <StyledLangGrid>
                      {langItems.map((item) => {
                        const isSelected = input.value === item.value;
                        const meta = LANG_META[item.value];
                        return (
                          <StyledLangCard
                            key={item.value}
                            selected={isSelected}
                            htmlFor={`lang-${item.value}`}
                          >
                            <StyledLangRadio selected={isSelected}>
                              <StyledLangRadioDot selected={isSelected} />
                            </StyledLangRadio>
                            <StyledLangBody>
                              <StyledLangName>{item.label}</StyledLangName>
                              {meta?.meta && (
                                <StyledLangMeta>{meta.meta}</StyledLangMeta>
                              )}
                            </StyledLangBody>
                            {meta?.flag && (
                              <span style={{ fontSize: 24, lineHeight: 1 }}>{meta.flag}</span>
                            )}
                            <input
                              id={`lang-${item.value}`}
                              type='radio'
                              name={input.name}
                              value={item.value}
                              checked={isSelected}
                              onChange={() => {
                                input.onChange(item.value);
                                setLanguage(item.value);
                              }}
                              disabled={isUserLoading || submitting}
                              style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                            />
                          </StyledLangCard>
                        );
                      })}
                    </StyledLangGrid>
                  )}
                </Field>
              </StyledFullWidthRow>
            </StyledMainGrid>

            <StyledActions>
              <Button type='submit' disabled={isUserLoading || submitting}>
                <FormattedMessage
                  id='forms.buttons.saveChanges'
                  defaultMessage='Save changes'
                />
              </Button>
              <Button
                type='button'
                kind={ButtonKind.Ghost}
                disabled={isUserLoading || submitting}
                onClick={() => {}}
              >
                <FormattedMessage
                  id='forms.buttons.cancel'
                  defaultMessage='Cancel'
                />
              </Button>
            </StyledActions>
          </form>
        )}
      </Form>
    </ScCard>
  );
};

export default AccountDetailsPage;
